import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { FooterContactUsSingleElementState } from './FooterContactUsSingleElement.state';
import { FooterContactUsElementsState } from 'src/main/cms/FooterManagement/FooterManagement';
import { FooterContactUsDataModelType } from 'src/api/config/footerManagement/footerContactUsElements/getContactUsElements';
import { Checkbox } from 'src/components/Checkbox/Checkbox';
import {
    FormWrapper,
    ControlsContainer,
    ActiveLabel,
    ButtonWrapper,
    ActionButtonsWrapper,
    FooterContactUsElementEditForm,
    InputRowSeparator,
} from 'src/main/cms/FooterManagement/FooterContactUs/FooterContactUsElementsForm.style';
import {
    ArrowIconWrapper,
    OrderItem,
    OrderWithArrowWrapper,
    ActionsWrapper,
    TrashIconWrapper,
} from 'src/domains/layouts/shared/cms/list/List.style';
import { contactTypeSet } from './FooterContactUsType';
import { Input } from 'src/domains/layouts/shared/formComponents/input/Input';
import { useAppStateContext } from 'src/AppState/AppState';

interface MoveUpDownArrowsPropsType {
    contactUsState: FooterContactUsElementsState;
    contactUsElement: FooterContactUsDataModelType;
    moveContactItemUp: (id: number) => Promise<void>;
    moveContactItemDown: (id: number) => Promise<void>;
}

const MoveUpDownArrows = observer(
    'MoveUpDownArrows',
    ({ contactUsState, contactUsElement, moveContactItemUp, moveContactItemDown }: MoveUpDownArrowsPropsType) => {
        const contactUsElements = contactUsState.contactUsElementsData;

        const firstElem = contactUsElements[0];
        const lastElem = contactUsElements[contactUsElements.length - 1];

        const isDisplayOrderTheLowest = contactUsElement.display_order === firstElem?.display_order;
        const isDisplayOrderTheHighest = contactUsElement.display_order === lastElem?.display_order;

        return (
            <ActionsWrapper>
                <OrderWithArrowWrapper
                    onClick={(): Promise<void> => moveContactItemUp(contactUsElement.id)}
                    isDisabled={isDisplayOrderTheLowest}
                >
                    <ArrowIconWrapper position='up' />
                    <OrderItem>Up</OrderItem>
                </OrderWithArrowWrapper>
                <OrderWithArrowWrapper
                    onClick={(): Promise<void> => moveContactItemDown(contactUsElement.id)}
                    isDisabled={isDisplayOrderTheHighest}
                >
                    <ArrowIconWrapper position='down' />
                    <OrderItem>Down</OrderItem>
                </OrderWithArrowWrapper>
            </ActionsWrapper>
        );
    }
);

interface FooterContactUsSingleElementPropsType {
    contactUsState: FooterContactUsElementsState;
    contactUsElement: FooterContactUsDataModelType;
}

export const FooterContactUsSingleElement = observer(
    'FooterContactUsSingleElement',
    ({ contactUsState, contactUsElement }: FooterContactUsSingleElementPropsType) => {
        const { trpcClient } = useAppStateContext();
        const [footerContactUsElementsState] = useState(
            () => new FooterContactUsSingleElementState(contactUsElement, contactUsState, trpcClient)
        );
        const {
            contactValue,
            contactLabel,
            isActive,
            setIsActive,
            handleEdit,
            handleDelete,
            moveContactItemUp,
            moveContactItemDown,
            hasInputChanged,
        } = footerContactUsElementsState;

        const contactType = contactUsElement.contact_type;

        return (
            <>
                <FormWrapper data-test={`footer-contact-us-existing-element-${contactUsElement.id}`}>
                    <FooterContactUsElementEditForm
                        onSubmit={(e: React.FormEvent<HTMLFormElement>): void => {
                            e.preventDefault();
                        }}
                    >
                        {contactTypeSet(contactType)?.icon}
                        <Input
                            label='Label'
                            formInputState={contactLabel}
                            dataTest='footer-contact-us-input-label'
                        />
                        <Input
                            label={contactTypeSet(contactType)?.label}
                            formInputState={contactValue}
                            dataTest='footer-contact-us-input'
                        />
                        <ControlsContainer>
                            <Checkbox
                                labelSide='right'
                                onChange={(): Promise<void> => setIsActive(contactUsElement.id)}
                                isChecked={isActive.value}
                                isDisabled={false}
                                dataTest='footer-contact-us-checkbox'
                            >
                                <ActiveLabel>Is active</ActiveLabel>
                            </Checkbox>
                            <MoveUpDownArrows
                                contactUsElement={contactUsElement}
                                contactUsState={contactUsState}
                                moveContactItemUp={moveContactItemUp}
                                moveContactItemDown={moveContactItemDown}
                            />
                        </ControlsContainer>
                    </FooterContactUsElementEditForm>
                    <ActionButtonsWrapper>
                        <>
                            {hasInputChanged === false ? null : (
                                <ButtonWrapper
                                    onClick={(): Promise<void> => handleEdit(contactType)}
                                    dataTest='footer-contact-us-update-button'
                                >
                                    Update
                                </ButtonWrapper>
                            )}
                            <TrashIconWrapper onClick={(): Promise<void> => handleDelete(contactUsElement.id)} />
                        </>
                    </ActionButtonsWrapper>
                </FormWrapper>
                <InputRowSeparator />
            </>
        );
    }
);
